import { createClient } from "@aluume/shared/lib/supabase/client";

// Email/password auth for the CUSTOMER app's single sign-in — the one form behind the
// profile icon, shared by travel agents and "100 by Aluume" founder members.
//
// There was never more than one credential check: lib/auth/agent.ts and lib/auth/member.ts
// are byte-identical calls to the same supabase.auth.signInWithPassword. What differed was
// only the COPY around them and where they sent you afterwards, which is why a founder
// signing in was told they were about to "book on account and view your statements". This
// module is that single call, named for what it actually is.
//
// WHO YOU ARE IS NOT DECIDED HERE. Sign-in proves the credential and nothing else; /account
// then resolves the live `agents` / `members` rows server-side and routes accordingly. A JWT
// claim is never the authority — see migration 0288's authorization-model header for why the
// server re-reads the live row rather than trusting app_role.
//
// agent.ts and member.ts are deliberately left in place, trimmed to the one thing that is
// genuinely per-portal: sign-OUT, which each portal's nav calls. Sign-in and password-reset
// were removed from them so there is exactly ONE definition of each in the codebase — a
// second copy of "where does a reset link land" is precisely how the founder-lands-on-the-
// agent-reset-screen bug survived as long as it did.

export async function signInToAccount(email: string, password: string) {
  const supabase = createClient();
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim(),
    password,
  });
  if (error) throw error;
  return data.session;
}

export async function requestAccountPasswordReset(email: string) {
  const supabase = createClient();
  // ⚠️ `redirectTo` IS LARGELY DECORATIVE HERE. The project ships a CUSTOM recovery template
  // whose href is built from {{ .SiteURL }} and {{ .TokenHash }}, so the address a recipient
  // actually gets is decided in Supabase's auth config, not here. That template now points at
  //   {{ .SiteURL }}/account/reset?token_hash=…&type=recovery
  // i.e. straight at the set-password screen, which verifies the token itself and then signs
  // the member in — no /auth/confirm hop, no second login (owner, 2026-09-02).
  //
  // It is kept, and kept in step with that template, so the two never tell different stories:
  // if the custom template is ever cleared, Supabase falls back to its default and THIS value
  // becomes the destination again.
  //
  // /account/reset is the ONE set-password screen and serves an agent and a founder alike —
  // which matters because the address is all we have here: asking for a reset does not say
  // which kind of account it belongs to, so the target must not assume one.
  const origin = process.env.NEXT_PUBLIC_APP_URL ?? window.location.origin;
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: `${origin}/account/reset`,
  });
  if (error) throw error;
}
